import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { currentUserAtom, authLoadedAtom } from '../auth/AuthAtoms';
import { packCountAtom } from '../atoms/packAtoms';
import { unreadMessageCountAtom } from '../atoms/messageAtoms';
import { fetchUnreadCount as fetchUnreadMessageCount } from '../api/messages';
import GLOBALS from '../config/globals';
import { handleLogout } from '../auth/AuthHelper';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faXmark, faUser, faChevronDown, faStore, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import NotificationBell from './NotificationBell';

function Navbar() {
    const [currentUser] = useAtom(currentUserAtom);
    const [authLoaded] = useAtom(authLoadedAtom);
    const packCount = useAtomValue(packCountAtom);
    const [unreadMessageCount, setUnreadMessageCount] = useAtom(unreadMessageCountAtom);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
    const [playDropdownOpen, setPlayDropdownOpen] = useState(false);
    const [leaderboardDropdownOpen, setLeaderboardDropdownOpen] = useState(false);
    const location = useLocation();
    const adminRef = useRef(null);
    const playRef = useRef(null);
    const leaderboardRef = useRef(null);

    useEffect(() => {
        setMobileMenuOpen(false);
        setAdminDropdownOpen(false);
        setPlayDropdownOpen(false);
        setLeaderboardDropdownOpen(false);
    }, [location]);

    // Re-fetch message unread count on route change
    useEffect(() => {
        if (!currentUser) return;
        let cancelled = false;
        fetchUnreadMessageCount().then(count => {
            if (!cancelled) setUnreadMessageCount(count);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [location.pathname, currentUser, setUnreadMessageCount]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (adminRef.current && !adminRef.current.contains(event.target)) {
                setAdminDropdownOpen(false);
            }
            if (playRef.current && !playRef.current.contains(event.target)) {
                setPlayDropdownOpen(false);
            }
            if (leaderboardRef.current && !leaderboardRef.current.contains(event.target)) {
                setLeaderboardDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const onLogout = useCallback(async () => {
        await handleLogout();
        window.location.href = '/';
    }, []);

    if (!authLoaded) return null;

    const playLinks = [
        { to: "/tournament", label: "Tournament", match: (p) => p.startsWith('/tournament') },
        { to: "/campaigns", label: "Campaigns", match: (p) => p === '/campaigns' || p.startsWith('/campaigns/') },
        { to: "/playoff", label: "Playoff Push", match: (p) => p.startsWith('/playoff'), requiresAuth: true },
        { to: "/games/user", label: "My Games", match: (p) => p.startsWith('/games'), requiresAuth: true }
    ];
    const visiblePlayLinks = playLinks.filter(l => !l.requiresAuth || currentUser);
    const playActive = visiblePlayLinks.some(l => l.match(location.pathname));

    const leaderboardLinks = [
        { to: "/leaderboard/monthly", label: "Player Stats", match: (p) => p === '/leaderboard' || p.startsWith('/leaderboard/monthly') },
        { to: "/leaderboard/elo", label: "Team Rating", match: (p) => p === '/leaderboard/elo' },
        { to: "/leaderboard/tournament-points", label: "Tournament Points", match: (p) => p === '/leaderboard/tournament-points' },
        { to: "/leaderboard/playoff-points", label: "Playoff Leaderboard", match: (p) => p === '/leaderboard/playoff-points' },
    ];
    const leaderboardActive = leaderboardLinks.some(l => l.match(location.pathname));

    const adminLinks = [
        { to: "/admin/series-management", label: "Series" },
        { to: "/admin/cards/bulk", label: "Bulk Cards" },
        { to: "/admin/cards/custom", label: "Custom Card" },
        { to: "/admin/roll-page", label: "Roll Dice" },
        { to: "/team/view-comp-teams", label: "Comp Team" },
        { to: "/rewards/", label: "Rewards" },
        { to: "/admin/users", label: "Users" },
        { to: "/admin/variations", label: "Variations" },
        { to: "/admin/series-variations", label: "Series Variations" },
        { to: "/admin/badges", label: "Badges" },
        { to: "/campaigns/create", label: "Create Campaign" },
        { to: "/campaign-contests/create", label: "Create Contest" },
    ];

    return (
        <header className="app-header">
            <div className="header-container">
                {/* 1. BRAND ICON */}
                <div className="header-brand">
                    <Link to="/" className="brand-icon-link" title="Home">
                        <img src="/img/uranum_neon.png" alt="Uranium Baseball" className="brand-logo" />
                    </Link>
                </div>

                {/* 2. DESKTOP NAVIGATION */}
                <nav className="desktop-nav">
                    <div className="play-menu-container" ref={playRef}>
                        <button
                            className={`play-trigger ${playActive || playDropdownOpen ? 'active' : ''}`}
                            onClick={() => setPlayDropdownOpen(!playDropdownOpen)}
                        >
                            Play <FontAwesomeIcon icon={faChevronDown} className="chevron" />
                        </button>
                        {playDropdownOpen && (
                            <div className="play-dropdown-menu">
                                {visiblePlayLinks.map(link => (
                                    <Link key={link.to} to={link.to} className={link.match(location.pathname) ? 'active' : ''}>{link.label}</Link>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="play-menu-container" ref={leaderboardRef}>
                        <button
                            className={`play-trigger ${leaderboardActive || leaderboardDropdownOpen ? 'active' : ''}`}
                            onClick={() => setLeaderboardDropdownOpen(!leaderboardDropdownOpen)}
                        >
                            Leaderboard <FontAwesomeIcon icon={faChevronDown} className="chevron" />
                        </button>
                        {leaderboardDropdownOpen && (
                            <div className="play-dropdown-menu">
                                {leaderboardLinks.map(link => (
                                    <Link key={link.to} to={link.to} className={link.match(location.pathname) ? 'active' : ''}>{link.label}</Link>
                                ))}
                            </div>
                        )}
                    </div>

                    {currentUser && (
                        <>
                            <Link to="/team/user" className={location.pathname.startsWith('/team') ? 'active' : ''}>Teams</Link>
                            <Link to="/cards/user" className={location.pathname.startsWith('/cards') ? 'active' : ''}>Cards</Link>
                            <Link to="/packs/user" className={`pack-link ${location.pathname.startsWith('/packs') ? 'active' : ''}`}>
                                Packs {packCount > 0 && <span className="pack-badge">{packCount}</span>}
                            </Link>
                        </>
                    )}
                    
                    {currentUser && currentUser.isAdmin && (
                        <div className="admin-menu-container" ref={adminRef}>
                            <button 
                                className={`admin-trigger ${adminDropdownOpen ? 'active' : ''}`}
                                onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                            >
                                Admin <FontAwesomeIcon icon={faChevronDown} className="chevron" />
                            </button>
                            {adminDropdownOpen && (
                                <div className="admin-dropdown-menu">
                                    {adminLinks.map(link => (
                                        <Link key={link.to} to={link.to}>{link.label}</Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </nav>

                {/* 3. USER UTILITIES */}
                <div className="header-utils">
                    <div className="nav-icon-group">
                        <a href={GLOBALS.DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" className="nav-icon discord" title="Discord">
                            <FontAwesomeIcon icon={faDiscord} />
                        </a>
                        <a href="https://www.etsy.com/shop/ThirdDanArt" target="_blank" rel="noopener noreferrer" className="nav-icon store" title="Etsy Store">
                            <FontAwesomeIcon icon={faStore} />
                        </a>
                        {currentUser && (
                            <>
                                <NotificationBell />
                                <Link to="/messages" className="nav-icon" title="Messages">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    {unreadMessageCount > 0 && (
                                        <span className="nav-badge">{unreadMessageCount}</span>
                                    )}
                                </Link>
                            </>
                        )}
                    </div>

                    {currentUser ? (
                        <>
                            <Link to="/account" className="util-item user">
                                <FontAwesomeIcon icon={faUser} />
                                <span className="desktop-only">{currentUser.username}</span>
                            </Link>
                            <button onClick={onLogout} className="btn-logout desktop-only">Logout</button>
                        </>
                    ) : (
                        <Link to="/login" className="btn-login">Log In</Link>
                    )}

                    <button 
                        className="mobile-toggle" 
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    >
                        <FontAwesomeIcon icon={mobileMenuOpen ? faXmark : faBars} />
                    </button>
                </div>
            </div>

            {mobileMenuOpen && (
                <div className="mobile-menu">
                    <nav className="mobile-nav-links">
                        <span className="mobile-section-label">Play</span>
                        {visiblePlayLinks.map(link => (
                            <Link key={link.to} to={link.to}>{link.label}</Link>
                        ))}
                        <span className="mobile-section-label">Leaderboard</span>
                        {leaderboardLinks.map(link => (
                            <Link key={link.to} to={link.to}>{link.label}</Link>
                        ))}
                        {currentUser && (
                            <>
                                <Link to="/team/user">Teams</Link>
                                <Link to="/cards/user">Cards</Link>
                                <Link to="/packs/user">Packs ({packCount})</Link>
                                <Link to="/messages">
                                    Messages {unreadMessageCount > 0 && <span className="pack-badge">{unreadMessageCount}</span>}
                                </Link>
                                <hr />
                                <Link to="/account">My Account</Link>
                                {currentUser.isAdmin && (
                                    <>
                                        <hr />
                                        <span className="mobile-admin-label">Admin Management</span>
                                        {adminLinks.map(link => (
                                            <Link key={link.to} to={link.to} className="mobile-admin-link">{link.label}</Link>
                                        ))}
                                    </>
                                )}
                                <button onClick={onLogout}>Logout</button>
                            </>
                        )}
                        {!currentUser && <Link to="/login">Log In</Link>}
                        <hr />
                        <div className="mobile-socials">
                            <a href={GLOBALS.DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer">Discord</a>
                            <a href="https://www.etsy.com/shop/ThirdDanArt" target="_blank" rel="noopener noreferrer">Etsy Store</a>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}

export default Navbar;
